/**
 * CotizacionPublica.jsx
 * Página pública que ve el cliente al abrir el link de aprobación.
 * No requiere autenticación — el token es el mecanismo de acceso.
 * Lee la propuesta con la RPC cotizacion_publica(token), que valida el token y
 * devuelve solo los campos que se muestran aquí (anon no lee la tabla).
 *
 * URL: https://app.tuaplicacion.cl/?token=<token> (los enlaces antiguos http:// redirigen a https://)
 */

import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { Check, X, ChevronLeft, Loader, FileText, AlertCircle, Download } from "lucide-react";

const N8N_WEBHOOK_RESPONDIDA = "https://tuaplicacion.app.n8n.cloud/webhook/cotizacion-respondida";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
};

const iStyle = {
  width:"100%", padding:"8px 12px", borderRadius:7,
  border:`1px solid ${C.border}`, background:C.white,
  color:C.text, fontSize:14, outline:"none",
  transition:"border-color .15s, box-shadow .15s",
  fontFamily:"'DM Sans',sans-serif",
};

const iTotal  = (i)     => i.horas * i.ph;
const calcSub = (items) => items.reduce((s,i)=>s+iTotal(i),0);
const calcIVA = (items) => Math.round(calcSub(items)*0.19);
const calcTot = (items) => calcSub(items)+calcIVA(items);
const fmtCLP  = (n)     => `$${n.toLocaleString("es-CL")}`;
const fmtDate = (d)     => d?new Date(d+"T12:00:00").toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric"}):"";

export default function CotizacionPublica() {
  const token     = new URLSearchParams(window.location.search).get("token");
  const printMode = new URLSearchParams(window.location.search).get("print") === "1";

  const [quote,   setQuote]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [step,    setStep]    = useState("view");   // view | confirm | done
  const [pending, setPending] = useState(null);
  const [comment, setComment] = useState("");
  const [checked, setChecked] = useState(false);
  const [firmado, setFirmado] = useState("");
  const [saving,  setSaving]  = useState(false);

  // ── Auto-print cuando viene con ?print=1 ──
  useEffect(() => {
    if (!printMode || !quote) return;
    const t = setTimeout(() => window.print(), 800);
    return () => clearTimeout(t);
  }, [printMode, quote]);

  // ── Cargar cotización por token ──
  useEffect(() => {
    if (!token) { setError("Link inválido o sin token."); setLoading(false); return; }

    (async () => {
      const { data, error: err } = await supabase.rpc("cotizacion_publica", { p_token: token });

      if (err) {
        console.error(err);
        setError("No pudimos cargar la propuesta. Revisa tu conexión e intenta nuevamente.");
      } else if (!data) {
        setError("Cotización no encontrada. Verifica el link.");
      } else {
        setQuote(data);
        if (["Aprobada","Rechazada"].includes(data.estado)) setStep("done");
      }
      setLoading(false);
    })();
  }, [token]);

  // ── Confirmar aprobación / rechazo ──
  const confirm = async () => {
    setSaving(true);

    const { data, error: err } = await supabase.rpc("responder_cotizacion", {
      p_token:      token,
      p_estado:     pending,
      p_comentario: comment,
    });

    if (err || (data && !data.success)) {
      setError(data?.error || "No se pudo registrar tu respuesta. Intenta de nuevo.");
      setSaving(false); return;
    }

    // Notificar a n8n → notifica al equipo de tuaplicacion
    try {
      await fetch(N8N_WEBHOOK_RESPONDIDA, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cotizacion_id: quote.id,
          cliente:       quote.cliente,
          email_cliente: quote.email,
          estado:        pending,
          comentario:    comment,
          firmado_por:   firmado,
          total_clp:     calcTot(quote.items||[]),
        }),
      });
    } catch (e) { console.warn("n8n webhook no disponible:", e); }

    setQuote(prev => ({ ...prev, estado: pending, comentario: comment }));
    setStep("done");
    setSaving(false);
  };

  // ── Estados de carga / error ──
  if (loading) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');@keyframes spin{to{transform:rotate(360deg)}}.spinning{animation:spin 1s linear infinite}`}</style>
      <div style={{textAlign:"center",color:C.sub}}>
        <Loader size={28} color={C.accent} className="spinning" style={{margin:"0 auto 12px"}}/>
        <div style={{fontSize:14}}>Cargando propuesta...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif",padding:24}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
      <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,padding:"32px 28px",maxWidth:420,textAlign:"center",boxShadow:"0 4px 20px #0000000f"}}>
        <AlertCircle size={36} color={C.err} style={{margin:"0 auto 14px"}}/>
        <div style={{fontWeight:700,fontSize:16,color:C.text,marginBottom:8}}>Propuesta no disponible</div>
        <div style={{fontSize:14,color:C.sub,lineHeight:1.6}}>{error}</div>
        <div style={{marginTop:20,fontSize:12,color:C.light}}>¿Problemas? Contacta a <strong style={{color:C.accent}}>contacto@tuaplicacion.cl</strong></div>
      </div>
    </div>
  );

  const items = quote.items || [];
  const sub = calcSub(items);
  const iv  = calcIVA(items);
  const tot = calcTot(items);
  const conIva = quote.con_iva !== false;
  const isDecided = ["Aprobada","Rechazada"].includes(quote.estado);
  const canDecide = quote.estado === "Pendiente";

  return (
    <div style={{minHeight:"100vh",background:"#f0f6fb",fontFamily:"'DM Sans',sans-serif",color:C.text}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');*{box-sizing:border-box;margin:0;padding:0}input:focus,textarea:focus{outline:none;border-color:#29abe2!important;box-shadow:0 0 0 3px #29abe215}@keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}@keyframes spin{to{transform:rotate(360deg)}}.fade-up{animation:fadeUp .22s ease both}.spinning{animation:spin 1s linear infinite}@media print{.no-print{display:none!important}.acciones-cotizacion{display:none!important}body{background:white!important}.item-cotizacion{page-break-inside:avoid}}`}</style>

      {/* Header */}
      <div style={{background:C.white,borderBottom:`1px solid ${C.border}`,padding:"0 32px",height:56,
        display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"0 1px 4px #0000000a"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:30,height:30,borderRadius:8,background:C.accent,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <FileText size={15} color="#fff"/>
          </div>
          <div>
            <span style={{fontWeight:800,fontSize:15,color:C.accent}}>tu</span>
            <span style={{fontWeight:800,fontSize:15,color:C.text}}>aplicacion</span>
          </div>
          <span style={{fontSize:11,color:C.sub,marginLeft:4}}>· Propuesta Comercial</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button className="no-print" onClick={() => window.print()}
            style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:7,
              border:`1px solid ${C.border}`,background:C.white,color:C.sub,
              fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",
              boxShadow:"0 1px 3px #0000000a",transition:"background .12s"}}
            onMouseEnter={e=>e.currentTarget.style.background=C.bg}
            onMouseLeave={e=>e.currentTarget.style.background=C.white}>
            <Download size={13}/> Descargar PDF
          </button>
          {/* Estado badge */}
          {isDecided && (
            <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 12px",borderRadius:20,fontSize:12,fontWeight:600,
              color:quote.estado==="Aprobada"?C.ok:C.err,
              background:quote.estado==="Aprobada"?C.okL:C.errL}}>
              {quote.estado==="Aprobada"?<Check size={12}/>:<X size={12}/>}
              {quote.estado}
            </span>
          )}
        </div>
      </div>

      <div style={{maxWidth:680,margin:"0 auto",padding:"32px 20px"}}>

        {/* Banner si ya fue respondida */}
        {isDecided && (
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"14px 18px",borderRadius:10,marginBottom:20,
            fontWeight:600,fontSize:14,
            background:quote.estado==="Aprobada"?C.okL:C.errL,
            border:`1px solid ${quote.estado==="Aprobada"?C.ok:C.err}40`,
            color:quote.estado==="Aprobada"?C.ok:C.err}}>
            {quote.estado==="Aprobada"?<Check size={16}/>:<X size={16}/>}
            {quote.estado==="Aprobada"?"Esta propuesta ya fue aprobada":"Esta propuesta fue rechazada"}
            {quote.comentario&&<span style={{fontSize:13,fontWeight:400,marginLeft:4,opacity:.75}}>— "{quote.comentario}"</span>}
          </div>
        )}

        {/* Expirada */}
        {quote.estado==="Expirada" && (
          <div style={{background:"#fef3c7",border:"1px solid #f59e0b40",borderRadius:10,padding:"14px 18px",marginBottom:20,
            fontWeight:600,fontSize:14,color:"#f59e0b"}}>
            ⌛ Esta propuesta ha expirado. Contacta a contacto@tuaplicacion.cl para renovarla.
          </div>
        )}

        {/* Card principal */}
        <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,padding:"22px 24px",marginBottom:14,boxShadow:"0 1px 3px #0000000a"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{fontSize:10,fontWeight:600,color:C.light,letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>
                Propuesta de Desarrollo de Software
              </div>
              <div style={{fontWeight:800,fontSize:24,color:C.text}}>{quote.id}</div>
              <div style={{fontSize:13,color:C.sub,marginTop:5}}>
                Preparada para <strong style={{color:C.text}}>{quote.cliente}</strong>
                {quote.rut&&<span style={{color:C.light}}> · RUT {quote.rut}</span>}
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontWeight:800,fontSize:28,color:C.accent}}>{fmtCLP(conIva ? tot : sub)}</div>
              <div style={{fontSize:11,color:C.sub,marginTop:3}}>{conIva ? "IVA incluido · CLP" : "Sin impuestos · CLP"}</div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginTop:18,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
            {[["Fecha emisión",fmtDate(quote.fecha)],["Válida hasta",fmtDate(quote.vence)],["Forma de pago",quote.condiciones]].map(([l,v])=>(
              <div key={l}>
                <div style={{fontSize:10,fontWeight:600,color:C.light,marginBottom:3,letterSpacing:.5,textTransform:"uppercase"}}>{l}</div>
                <div style={{fontSize:13,fontWeight:500,lineHeight:1.4}}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabla de ítems */}
        <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",marginBottom:14,boxShadow:"0 1px 3px #0000000a"}}>
          <div style={{padding:"13px 20px",borderBottom:`1px solid ${C.border}`,fontWeight:600,fontSize:13}}>
            Requerimientos incluidos
          </div>
          {items.map((item,idx)=>(
            <div key={item.id||idx} style={{display:"grid",gridTemplateColumns:"1fr 120px",gap:12,
              padding:"14px 20px",borderBottom:idx<items.length-1?`1px solid ${C.border}`:"none",alignItems:"center"}}>
              <div>
                <div style={{fontWeight:600,fontSize:14,marginBottom:6}}>{item.desc}</div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{padding:"2px 8px",borderRadius:4,background:C.accentL,color:C.accent,fontSize:11,fontWeight:600}}>{item.tipo}</span>
                  <span style={{fontSize:12,color:C.sub}}>{item.horas}h × {fmtCLP(item.ph)}/h</span>
                </div>
              </div>
              <div style={{fontWeight:700,fontSize:15,textAlign:"right",color:C.text}}>{fmtCLP(iTotal(item))}</div>
            </div>
          ))}
          <div style={{padding:"14px 20px",borderTop:`1px solid ${C.border}`,background:C.bg}}>
            {conIva
              ? [["Subtotal neto",fmtCLP(sub),false],["IVA 19%",fmtCLP(iv),false],["TOTAL",fmtCLP(tot),true]].map(([l,v,bold])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",
                  padding:bold?"10px 0 0":"3px 0",borderTop:bold?`2px solid ${C.border}`:"none",marginTop:bold?8:0}}>
                  <span style={{color:bold?C.text:C.sub,fontWeight:bold?700:400,fontSize:bold?16:13}}>{l}</span>
                  <span style={{color:bold?C.accent:C.sub,fontWeight:bold?800:400,fontSize:bold?20:13}}>{v}</span>
                </div>
              ))
              : <>
                  <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0 0",borderTop:`2px solid ${C.border}`,marginTop:8}}>
                    <span style={{color:C.text,fontWeight:700,fontSize:16}}>TOTAL</span>
                    <span style={{color:C.accent,fontWeight:800,fontSize:20}}>{fmtCLP(sub)}</span>
                  </div>
                  <div style={{fontSize:11,color:C.sub,marginTop:4}}>(Precio no afecto a IVA)</div>
                </>
            }
          </div>
        </div>

        {/* Notas */}
        {quote.notas&&(
          <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,padding:"15px 20px",marginBottom:16,boxShadow:"0 1px 3px #0000000a"}}>
            <div style={{fontSize:10,fontWeight:600,color:C.light,letterSpacing:.5,textTransform:"uppercase",marginBottom:5}}>Notas del proyecto</div>
            <div style={{fontSize:13,color:C.sub,lineHeight:1.6}}>{quote.notas}</div>
          </div>
        )}

        {/* ── Sección de decisión ── */}
        {canDecide && step==="view" && (
          <div className="acciones-cotizacion fade-up" style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,padding:"22px 24px",boxShadow:"0 1px 3px #0000000a"}}>
            <div style={{fontWeight:700,fontSize:15,marginBottom:5}}>¿Qué deseas hacer con esta propuesta?</div>
            <div style={{fontSize:13,color:C.sub,marginBottom:18,lineHeight:1.6}}>
              Al aprobar, notificaremos al equipo de tuaplicacion para coordinar el inicio del proyecto.
            </div>

            <div style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Tu nombre completo (firma simple) *</label>
              <input value={firmado} onChange={e=>setFirmado(e.target.value)}
                placeholder="Ej: Juan Pérez González" style={iStyle}/>
            </div>

            <label style={{display:"flex",alignItems:"flex-start",gap:8,cursor:"pointer",fontSize:13,color:C.sub,lineHeight:1.6,marginBottom:18}}>
              <input type="checkbox" checked={checked} onChange={e=>setChecked(e.target.checked)} style={{marginTop:3,accentColor:C.accent}}/>
              He revisado esta propuesta en detalle y acepto las condiciones y la forma de pago indicada. Entiendo que la aprobación constituye un compromiso de trabajo.
            </label>

            {(!checked||firmado.length<=2)&&(
              <div style={{fontSize:12,color:C.light,marginBottom:14,padding:"8px 12px",background:C.bg,borderRadius:6}}>
                ✦ Ingresa tu nombre y acepta las condiciones para continuar.
              </div>
            )}

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <button
                onClick={()=>{if(checked&&firmado.length>2){setPending("Aprobada");setStep("confirm");}}}
                style={{padding:"15px",borderRadius:10,fontWeight:600,fontSize:14,
                  cursor:checked&&firmado.length>2?"pointer":"not-allowed",
                  border:`2px solid ${checked&&firmado.length>2?C.ok:C.border}`,
                  background:checked&&firmado.length>2?C.okL:"#f8fafc",
                  color:checked&&firmado.length>2?C.ok:C.light,
                  opacity:checked&&firmado.length>2?1:.45,
                  transition:"all .15s",fontFamily:"'DM Sans',sans-serif",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                <Check size={16}/> Aprobar propuesta
              </button>
              <button onClick={()=>{setPending("Rechazada");setStep("confirm");}}
                style={{padding:"15px",borderRadius:10,fontWeight:600,fontSize:14,
                  border:`2px solid ${C.border}`,background:"transparent",color:C.sub,
                  cursor:"pointer",transition:"all .15s",fontFamily:"'DM Sans',sans-serif",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                <X size={16}/> Rechazar propuesta
              </button>
            </div>
          </div>
        )}

        {/* Confirmar */}
        {step==="confirm" && (
          <div style={{background:C.white,borderRadius:12,padding:"22px 24px",
            border:`1px solid ${pending==="Aprobada"?C.ok:C.err}40`,boxShadow:"0 1px 3px #0000000a"}} className="fade-up">
            <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>
              {pending==="Aprobada"?"✓ Confirmar aprobación":"Confirmar rechazo"}
            </div>
            <div style={{fontSize:13,color:C.sub,marginBottom:16,lineHeight:1.6}}>
              {pending==="Aprobada"
                ?`Al confirmar, notificaremos al equipo para dar inicio al proyecto. Quedará registrado con tu firma: "${firmado}".`
                :"Si deseas, puedes dejar un comentario para que el equipo entienda los motivos."}
            </div>
            {pending==="Rechazada"&&(
              <textarea value={comment} onChange={e=>setComment(e.target.value)} rows={3}
                placeholder="Motivo del rechazo (opcional)..."
                style={{...iStyle,resize:"vertical",marginBottom:14}}/>
            )}
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setStep("view")}
                style={{padding:"8px 18px",borderRadius:7,border:`1px solid ${C.border}`,
                  background:"transparent",color:C.sub,fontSize:13,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                ← Volver
              </button>
              <button onClick={confirm} disabled={saving}
                style={{display:"flex",alignItems:"center",gap:6,padding:"8px 22px",borderRadius:7,border:"none",
                  background:saving?"#94a3b8":(pending==="Aprobada"?C.ok:C.err),
                  color:"#fff",fontWeight:600,fontSize:13,cursor:saving?"not-allowed":"pointer",fontFamily:"'DM Sans',sans-serif",
                  boxShadow:saving?"none":`0 4px 12px ${pending==="Aprobada"?C.ok:C.err}40`}}>
                {saving?<><Loader size={13} className="spinning"/>Procesando...</>:(pending==="Aprobada"?"Sí, aprobar":"Sí, rechazar")}
              </button>
            </div>
          </div>
        )}

        {/* ── Pantalla final ── */}
        {step==="done" && (
          <div style={{background:quote.estado==="Aprobada"||pending==="Aprobada"?C.okL:C.errL,
            borderRadius:12,padding:"36px 24px",textAlign:"center",
            border:`1px solid ${quote.estado==="Aprobada"||pending==="Aprobada"?C.ok:C.err}40`}} className="fade-up">
            <div style={{width:52,height:52,borderRadius:"50%",
              background:quote.estado==="Aprobada"||pending==="Aprobada"?C.ok:C.err,
              display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px",
              boxShadow:`0 4px 16px ${quote.estado==="Aprobada"||pending==="Aprobada"?C.ok:C.err}50`}}>
              {quote.estado==="Aprobada"||pending==="Aprobada"?<Check size={24} color="#fff"/>:<X size={24} color="#fff"/>}
            </div>
            <div style={{fontWeight:800,fontSize:18,color:quote.estado==="Aprobada"||pending==="Aprobada"?C.ok:C.err,marginBottom:10}}>
              {quote.estado==="Aprobada"||pending==="Aprobada"?"¡Propuesta aprobada!":"Propuesta rechazada"}
            </div>
            <div style={{fontSize:14,color:C.sub,lineHeight:1.7,maxWidth:380,margin:"0 auto"}}>
              {quote.estado==="Aprobada"||pending==="Aprobada"
                ?"Hemos notificado al equipo de tuaplicacion. Nos pondremos en contacto contigo a la brevedad para coordinar los próximos pasos."
                :"Hemos registrado tu decisión. Si deseas revisar la propuesta con ajustes, no dudes en contactarnos."}
            </div>
            <div style={{marginTop:20,padding:"12px 18px",background:"#ffffff60",borderRadius:8,display:"inline-block",fontSize:13,color:C.sub}}>
              Para cualquier consulta: <strong style={{color:C.accent}}>contacto@tuaplicacion.cl</strong>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{textAlign:"center",marginTop:32,paddingTop:18,borderTop:`1px solid ${C.border}`}}>
          <div style={{fontSize:13,fontWeight:700}}>
            <span style={{color:C.accent}}>tu</span>
            <span style={{color:C.sub}}>aplicacion</span>
            <span style={{color:C.light,fontWeight:400}}> · Empresa de Desarrollo de Software · Chile</span>
          </div>
          <div style={{fontSize:11,color:C.light,marginTop:5}}>contacto@tuaplicacion.cl · www.tuaplicacion.cl</div>
        </div>
      </div>
    </div>
  );
}
